import React, { useRef, useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useSolrContext } from "@e-fork/solr";
import classNames from "classnames";
import { useLocation } from "react-router-dom";

import NoResults from "../components/system/no-results";
import Loading from "../components/system/loading";
import Error from "../components/system/error";
import EndlessScroll from "../components/system/endless-scroll";
import Pagination from "../components/system/pagination";
import Filters from "../components/filters";

import venueSchemaMapper from "@default/teasers/venue/venue-schema-mapper";
import TeaserVenueImage from "@default/teasers/venue/teaser-venue-image";
import { encodeUrl } from "@default/lib/encode-url";
import useScrollActive from "@default/hooks/use-scroll-active";
import useStickyFilterVanish from "@default/hooks/use-sticky-filter-vanish";
import VenueItem from "./components/venue-item";
import FilterModal from "../components/system/filter-modal";
import FilterModalToggle from "../components/system/filter-modal-toggle";
import useAccessibilityContext from "@default/js/accessibility/store/use-accessibility-context";
import useTranslate from "@default/hooks/use-translate";

const OverviewVenues = ({ content }) => {
  const { items, error, config, metaData, fetchMore, loading } =
    useSolrContext();
  const containerRef = useRef(null);
  const [activeId, setActiveId] = useState(null);
  const filterModal = useRef();
  const { sentinelRef, filterWrapperRef } = useStickyFilterVanish();
  const { simpleDesign } = useAccessibilityContext();
  const _t = useTranslate();
  const { hash } = useLocation();
  const [hashScrolled, setHashScrolled] = useState(false);
  // Guards against firing fetchMore() twice for the same missing-item state:
  // `loading` from useSolrContext may not flip to true before this effect's
  // next render, since it depends on React Query's internal timing.
  const fetchingRef = useRef(false);

  const mappedItems = useMemo(
    () => items?.map(venueSchemaMapper) ?? [],
    [items]
  );

  const handleActiveChange = useCallback((id) => setActiveId(id), []);

  useScrollActive(mappedItems, containerRef, handleActiveChange);

  useEffect(() => {
    if (!hash || hashScrolled || !mappedItems.length || loading) return;

    const targetId = hash.replace("#", "");
    const matchingItem = mappedItems.find(
      (item) => encodeUrl(item.label) === targetId
    );

    if (matchingItem) {
      const el = document.getElementById(
        encodeUrl(`${matchingItem.label}-${matchingItem.id}`)
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        setHashScrolled(true);
      }
      return;
    }

    // Not among the loaded items yet — load more pages until it turns up
    // or all results have been fetched.
    if (mappedItems.length < metaData.totalRows) {
      if (fetchingRef.current) return;
      fetchingRef.current = true;
      fetchMore().finally(() => {
        fetchingRef.current = false;
      });
    } else {
      setHashScrolled(true);
    }
  }, [hash, mappedItems, hashScrolled, loading, metaData, fetchMore]);

  return (
    <section
      className={classNames(
        "paragraph paragraph-overview overview-solr overview-venues",
        `type-${content.fieldContentType}`
      )}
    >
      <div className="container no-padding">
        <div ref={sentinelRef} />
        <div
          ref={filterWrapperRef}
          className="row sticky-wrapper filter-wrapper"
        >
          <div className="col-12 d-flex justify-content-center">
            <FilterModalToggle
              onClick={() => filterModal.current.open()}
              label={_t("overview.controls.filter_modal_toggle.locations")}
            />
            <FilterModal ref={filterModal}>
              <Filters />
            </FilterModal>
          </div>
        </div>

        {!!mappedItems.length && !error && (
          <div className="scroll-overview" ref={containerRef}>
            <div className="container content">
              <div className="row list">
                {/* Desktop sticky image pane — hidden on mobile via CSS */}
                {!simpleDesign && (
                  <div className="col-6 image-wrapper" aria-hidden="true">
                    <div className="absolute-wrapper">
                      <div className="sticky-wrapper">
                        <div className="movement-wrapper">
                          {mappedItems.map((item) => (
                            <TeaserVenueImage key={item.id} item={item} />
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Scrollable list pane */}
                <div
                  className={classNames("teaser-wrapper", {
                    "col-12 col-lg-6": !simpleDesign,
                    "col-12": simpleDesign,
                  })}
                >
                  <ol className="venues-list scroll-wrapper" role="list">
                    {mappedItems.map((item, index) => (
                      <VenueItem
                        key={item.id}
                        item={item}
                        index={index}
                        isActive={
                          activeId === encodeUrl(`${item.label}-${item.id}`)
                        }
                      />
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          <div className="col-12">
            <NoResults />
            <Loading />
            <Error />
          </div>
        </div>

        {config.pageMode === "pager" && <Pagination />}
        {config.pageMode === "endless" && <EndlessScroll />}
      </div>
    </section>
  );
};

OverviewVenues.propTypes = {
  content: PropTypes.shape({
    fieldContentType: PropTypes.string,
  }),
};

export default OverviewVenues;
